import { environment } from "../config/environment";
import { customFetch } from "./Auth";

const normalizeAudioUrl = (audioUrl) => {
  const rawUrl = String(audioUrl || "").trim();
  if (!rawUrl) return "";

  try {
    const parsedUrl = new URL(rawUrl);
    if (parsedUrl.hostname !== "s3.amazonaws.com") return rawUrl;

    const [bucketName] = parsedUrl.pathname.replace(/^\/+/, "").split("/");
    const clientRegion = String(environment.awsRegion || "").trim();
    const isGovBucket = bucketName?.includes("-gov");
    const isGovRegion = clientRegion.startsWith("us-gov-");
    if (!isGovBucket && !isGovRegion) return rawUrl;

    parsedUrl.hostname = `s3.${
      isGovRegion ? clientRegion : "us-gov-east-1"
    }.amazonaws.com`;
    return parsedUrl.toString();
  } catch (_error) {
    return rawUrl;
  }
};

const fetchJson = async (path, params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      searchParams.append(key, value);
    }
  });

  const queryString = searchParams.toString();
  const response = await customFetch(
    `${environment.apiUrl}${path}${queryString ? `?${queryString}` : ""}`,
    { method: "GET" }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Request failed");
  }
  return data;
};

const parseTranscriptCollection = (value) => {
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return [];

    try {
      return parseTranscriptCollection(JSON.parse(trimmedValue));
    } catch (_error) {
      return [];
    }
  }

  if (value && typeof value === "object") {
    for (const key of ["words", "chunks", "items", "segments", "results"]) {
      if (value[key] !== undefined) {
        return parseTranscriptCollection(value[key]);
      }
    }

    const entries = Object.entries(value);
    if (
      entries.length > 0 &&
      entries.every(([key, item]) => /^\d+$/.test(key) && item)
    ) {
      return entries
        .sort(([left], [right]) => Number(left) - Number(right))
        .map(([, item]) => item);
    }
  }

  return [];
};

export const normalizeTranscriptWords = (value) =>
  parseTranscriptCollection(value)
    .map((item) => {
      if (!item || typeof item !== "object") return null;

      const text =
        item.text ??
        item.word ??
        item.content ??
        item.transcript ??
        item.alternatives?.[0]?.content;
      const startValue = item.start ?? item.start_time ?? item.startTime;
      const endValue = item.end ?? item.end_time ?? item.endTime;
      const start = Number(startValue);
      const end = Number(endValue);

      if (text === null || text === undefined || !Number.isFinite(start)) {
        return null;
      }

      return {
        ...item,
        text: String(text),
        start,
        ...(Number.isFinite(end) ? { end } : {}),
      };
    })
    .filter(Boolean);

const normalizeSummarizeJob = (data = {}) => ({
  ...data,
  jobName: data.jobName || data.summarize_job_name || "",
  audio_url: normalizeAudioUrl(data.audio_url),
  original_transcript: data.original_transcript || "",
  soap_note: data.soap_note || "",
  llm_reviewed_soap_note: data.llm_reviewed_soap_note || "",
  original_transcript_words: normalizeTranscriptWords(
    data.original_transcript_words ??
      data.original_words ??
      data.transcript_words ??
      data.transcript_chunks
  ),
  soap_note_wrong_words: Array.isArray(data.soap_note_wrong_words)
    ? data.soap_note_wrong_words
    : [],
});

const normalizeTranscribeJob = (data = {}) => ({
  ...data,
  jobName: data.jobName || data.transcribe_job_name || "",
  audio_url: normalizeAudioUrl(data.audio_url),
  original_transcript_text:
    data.original_transcript_text || data.original_transcript || "",
});

export const fetchSummarizeJob = async (summarizeJobName) =>
  fetchJson("/oncall/summarize-job", {
    summarize_job_name: summarizeJobName,
  }).then(normalizeSummarizeJob);

export const fetchTranscribeJob = async (transcribeJobName) =>
  fetchJson("/oncall/transcribe-job", {
    transcribe_job_name: transcribeJobName,
  }).then(normalizeTranscribeJob);

export const searchSummarizeJobs = async (params) =>
  fetchJson("/oncall/summarize-jobs", params);
